import { Link } from 'react-router-dom';
import { useTrackEvent } from '../hooks/useDiscovery';
import type { PublicRecommendationWithAssets } from '../types';

interface RecommendationCardProps {
  recommendation: PublicRecommendationWithAssets;
}

export function RecommendationCard({ recommendation }: RecommendationCardProps) {
  const { track } = useTrackEvent();

  const handleClick = () => {
    track({ type: 'click', recommendationId: recommendation.id });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const primaryAsset = recommendation.assets?.[0];

  return (
    <article className="card group" onClick={handleClick}>
      <Link to={`/recommendations/${recommendation.id}`} className="block">
        {/* Image */}
        <div className="relative aspect-video overflow-hidden bg-gray-100">
          {primaryAsset && primaryAsset.url ? (
            <img
              src={primaryAsset.url}
              alt={recommendation.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className="absolute top-2 right-2 flex gap-1">
            {recommendation.category && (
              <span className="px-2 py-0.5 rounded-full bg-white/90 text-xs font-medium text-gray-900 backdrop-blur-sm">
                {recommendation.category.name}
              </span>
            )}
            {recommendation.city && (
              <span className="px-2 py-0.5 rounded-full bg-white/90 text-xs font-medium text-gray-900 backdrop-blur-sm">
                {recommendation.city.name}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-primary transition-colors flex-1 min-w-0">
              {recommendation.title}
            </h3>
            {recommendation.business && (
              <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600 whitespace-nowrap">
                {recommendation.business.name}
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 line-clamp-2">
            {recommendation.body || 'Sin descripción'}
          </p>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <time>{formatDate(recommendation.createdAt)}</time>
              {recommendation.counts && (
                <>
                  {recommendation.counts.views > 0 && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.074 4.057-4.867 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {recommendation.counts.views}
                    </span>
                  )}
                  {recommendation.counts.saves > 0 && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                      {recommendation.counts.saves}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}